import React, { useContext, useRef } from "react";
import { ResizeEnable, Rnd } from "react-rnd";
import OpenWithIcon from "@mui/icons-material/OpenWith";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { CanvasContext, ICanvasComponent } from "../canvasContext.tsx";
import { resizeHandleClasses } from "../canvasUtils.tsx";
import ImageElement from "./ImageElement.tsx";
import TextElement from "./TextElement.tsx";
import VideoElement from "./VideoElement.tsx";
import SlideElement from "./SlideElement.tsx";
import Model3DElement from "./Model3DElement.tsx";

const DRAG_HANDLE_CLASS = "canvas-drag-handle";

const componentMap: { [key: string]: React.ComponentType<ICanvasComponent> } = {
  TEXT: TextElement,
  IMAGE: ImageElement,
  VIDEO: VideoElement,
  SLIDE: SlideElement,
  "3D_MODEL": Model3DElement,
};

const toCssSize = (value?: string | number) => {
  if (value == null) return undefined;
  if (typeof value === "number") return `${value}px`;
  if (typeof value !== "string") return value;
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  return /^\d+(\.\d+)?$/.test(trimmed) ? `${trimmed}px` : trimmed;
};

const getEnableResize = (type: string): ResizeEnable => {
  const allowAll = ["IMAGE", "VIDEO", "SLIDE", "3D_MODEL"].includes(type);
  return {
    bottom: allowAll,
    bottomLeft: true,
    bottomRight: true,
    top: allowAll,
    topLeft: true,
    topRight: true,
    left: true,
    right: true,
  };
};
const CanvasComponent = (props: ICanvasComponent & { viewMode?: "web" | "mobile" }) => {
  const { state, actions } = useContext(CanvasContext);
  const {
    dimension,
    position,
    mobilePosition,
    mobileDimension,
    content,
    id,
    type,
    slideZoom,
    cameraOrbit,
    isBackground,
    isPlayMode,
    slideAnnotations,
    viewMode = "web",
  } = props;
  const [showGrids, setShowGrids] = React.useState(false);
  const [isReadOnly, setIsReadOnly] = React.useState(true);
  const [isDraggingActive, setIsDraggingActive] = React.useState(false);
  const elementRef = React.useRef<HTMLDivElement>(null);
  const isDragged = useRef<boolean>(false);

  const activeSelection = state?.activeSelection;
  const canvasData = state?.canvasData ?? [];
  const layerIndex = id ? canvasData.findIndex((c) => c.id === id) : -1;
  const canMoveUp = layerIndex >= 0 && layerIndex < canvasData.length - 1;
  const canMoveDown = layerIndex > 0;

  const onBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    const toolbarElement = document.querySelector("#toolbar");
    if (
      event.currentTarget.contains(event?.relatedTarget as Element) ||
      toolbarElement?.contains(event?.relatedTarget as Element)
    ) {
      return;
    }
    setIsReadOnly(true);
    actions?.setEnableQuillToolbar(false);
    if (id && activeSelection) {
      activeSelection.delete(id);
      actions?.setActiveSelection(new Set(activeSelection));
    }
    actions?.saveNow?.();
  };

  const effectivePosition =
    viewMode === "mobile" ? mobilePosition ?? position : position;

  const effectiveDimension =
    viewMode === "mobile" ? mobileDimension ?? dimension : dimension;

  const getComponent = () => {
    const Component = type && componentMap[type];
    if (!Component || !id) return null;
    return (
      <Component
        key={id}
        id={id}
        type={type}
        position={effectivePosition}
        dimension={effectiveDimension}
        content={content}
        isReadOnly={isPlayMode ? true : isReadOnly}
        isBackground={isBackground}
        isPlayMode={isPlayMode}
        slideZoom={slideZoom}
        cameraOrbit={cameraOrbit}
        slideAnnotations={slideAnnotations}
      />
    );
  };

  const style: React.CSSProperties = {
    outline: "none",
    border: `2px solid ${
      (id && state?.activeSelection.has(id)) || showGrids || isDragged.current
        ? "#06b6d4"
        : "transparent"
    }`
  };

  const onMouseEnter = () => {
    setShowGrids(true);
  };

  const onMouseLeave = () => {
    setShowGrids(false);
  };

  const onfocus = (event: React.MouseEvent) => {
    if (id) {
      actions?.setActiveSelection(new Set(state?.activeSelection.add(id)));
    }
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!isReadOnly) event.stopPropagation();
  };

  const handleClass =
    !isBackground && id && state?.activeSelection.has(id) && state?.activeSelection.size === 1
      ? "showHandles"
      : "";
  const hasViewerHeader =
    type === "SLIDE" || type === "3D_MODEL" || type === "IMAGE" || type === "VIDEO" || type === "TEXT";
  const headerLabel =
    type === "SLIDE"
      ? "Histology viewer"
      : type === "3D_MODEL"
        ? "3D model viewer"
        : type === "IMAGE"
          ? "Image"
          : type === "VIDEO"
            ? "Video"
            : type === "TEXT"
              ? "Text"
              : "";

  const onDoubleClick = () => {
    if (!isReadOnly) return;
    setIsReadOnly(false);
    actions?.setEnableQuillToolbar(true);
  };

  React.useEffect(() => {
    if (!isDraggingActive) return;
    const stopDragging = () => {
      actions?.endHistoryStep?.();
      isDragged.current = false;
      setIsDraggingActive(false);
    };
    window.addEventListener("mouseup", stopDragging);
    window.addEventListener("blur", stopDragging);
    return () => {
      window.removeEventListener("mouseup", stopDragging);
      window.removeEventListener("blur", stopDragging);
    };
  }, [isDraggingActive]);

  if (isPlayMode) {
    return (
      <div
        style={{
          position: "absolute",
          left: toCssSize(position?.left) || 0,
          top: toCssSize(position?.top) || 0,
          width: toCssSize(dimension?.width) || 0,
          height: toCssSize(dimension?.height) || 0,
          overflow: "hidden",
          pointerEvents: "auto",
        }}
      >
        <div style={{ position: "relative", width: "100%", height: "100%" }}>
          <div
            className="item-container"
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              zIndex: 0,
            }}
          >
            {getComponent()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={elementRef}>
      <Rnd
        style={style}
        size={{
          width: effectiveDimension?.width || 0,
          height: effectiveDimension?.height || 0,
        }}
        position={{
          x: effectivePosition?.left || 0,
          y: effectivePosition?.top || 0,
        }}
        onDragStart={() => {
          actions?.beginHistoryStep?.();
          isDragged.current = true;
          setIsDraggingActive(true);
        }}
        onDragStop={(e, d) => {
          isDragged.current = false;
          setIsDraggingActive(false);
          actions?.endHistoryStep?.();
          if (viewMode === "mobile") {
            actions?.updateCanvasData({
              id,
              mobilePosition: { left: d.x, top: d.y },
            });
          } else {
            actions?.updateCanvasData({
              id,
              position: { left: d.x, top: d.y },
            });
          }
          actions?.saveNow?.();
        }}
        resizeHandleWrapperClass={handleClass}
        resizeHandleClasses={resizeHandleClasses}
        onResize={(e, direction, ref, delta, position) => {
          if (viewMode === "mobile") {
            actions?.updateCanvasData({
              id,
              mobileDimension: {
                width: ref.style.width,
                height: ref.style.height,
              },
              mobilePosition: { top: position.y, left: position.x },
            });
          } else {
            actions?.updateCanvasData({
              id,
              dimension: {
                width: ref.style.width,
                height: ref.style.height,
              },
              position: { top: position.y, left: position.x },
            });
          }
        }}
        onResizeStop={() => {
          actions?.endHistoryStep?.();
          actions?.saveNow?.();
        }}
        onResizeStart={() => {
          actions?.beginHistoryStep?.();
        }}
        enableResizing={isBackground ? false : getEnableResize(type)}
        minWidth={100}
        minHeight={50}
        disableDragging={!!isBackground}
        dragHandleClassName={DRAG_HANDLE_CLASS}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        onDoubleClick={isBackground ? undefined : onDoubleClick}
        onKeyDown={onKeyDown}
        onFocus={isBackground ? undefined : onfocus}
        onBlur={isBackground ? undefined : onBlur}
        tabIndex={isBackground ? -1 : 0}
        lockAspectRatio={false}
      >
        <div style={{ position: "relative", width: "100%", height: "100%" }}>
          {!isBackground && !isPlayMode && (
            <div
              style={{
                position: "absolute",
                top: type === "TEXT" ? -26 : 4,
                left: hasViewerHeader ? 4 : "auto",
                right: 4,
                zIndex: 9999,
                display: "flex",
                alignItems: "center",
                gap: 4,
                pointerEvents: "auto",
                width: hasViewerHeader ? "calc(100% - 8px)" : "auto",
              }}
            >
              {hasViewerHeader ? (
                <div
                  className={DRAG_HANDLE_CLASS}
                  style={{
                    cursor: "move",
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    height: 34,
                    borderRadius: 10,
                    padding: "0 10px",
                    background: "linear-gradient(180deg, rgba(15,23,42,0.78), rgba(15,23,42,0.62))",
                    color: "#fff",
                    boxShadow: "0 8px 16px rgba(0,0,0,0.24)",
                    backdropFilter: "blur(10px)",
                    position: "relative",
                  }}
                  title="Drag to move"
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      minWidth: 0,
                      paddingRight: 132,
                    }}
                  >
                    <OpenWithIcon sx={{ fontSize: 18, flexShrink: 0 }} />
                    <span
                      style={{
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: "0.02em",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {headerLabel}
                    </span>
                  </div>
                  <div
                    style={{
                      position: "absolute",
                      top: 3,
                      right: 6,
                      display: "flex",
                      alignItems: "center",
                      gap: 4,
                    }}
                  >
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (id && canMoveUp) actions?.moveElementLayer?.(id, "up");
                      }}
                      onKeyDown={(e) => {
                        if ((e.key === "Enter" || e.key === " ") && id && canMoveUp) {
                          e.preventDefault();
                          actions?.moveElementLayer?.(id, "up");
                        }
                      }}
                      style={{
                        cursor: canMoveUp ? "pointer" : "not-allowed",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 28,
                        height: 28,
                        borderRadius: 4,
                        background: "rgba(255,255,255,0.14)",
                        color: canMoveUp ? "#fff" : "rgba(255,255,255,0.4)",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                      }}
                      title="Bring layer up"
                    >
                      <ArrowUpwardIcon sx={{ fontSize: 18 }} />
                    </div>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (id && canMoveDown) actions?.moveElementLayer?.(id, "down");
                      }}
                      onKeyDown={(e) => {
                        if ((e.key === "Enter" || e.key === " ") && id && canMoveDown) {
                          e.preventDefault();
                          actions?.moveElementLayer?.(id, "down");
                        }
                      }}
                      style={{
                        cursor: canMoveDown ? "pointer" : "not-allowed",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 28,
                        height: 28,
                        borderRadius: 4,
                        background: "rgba(255,255,255,0.14)",
                        color: canMoveDown ? "#fff" : "rgba(255,255,255,0.4)",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                      }}
                      title="Bring layer down"
                    >
                      <ArrowDownwardIcon sx={{ fontSize: 18 }} />
                    </div>
                    <div
                      role="button"
                      tabIndex={0}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (id) actions?.deleteElementById(id);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          if (id) actions?.deleteElementById(id);
                        }
                      }}
                      style={{
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        width: 28,
                        height: 28,
                        borderRadius: 4,
                        background: "rgba(255,255,255,0.14)",
                        color: "#fff",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                      }}
                      title="Delete"
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className={DRAG_HANDLE_CLASS}
                  style={{
                    cursor: "move",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 28,
                    height: 28,
                    borderRadius: 4,
                    background: "rgba(0,0,0,0.65)",
                    color: "#fff",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                  }}
                  title="Drag to move"
                >
                  <OpenWithIcon sx={{ fontSize: 18 }} />
                </div>
              )}
              {!hasViewerHeader && (
                <>
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  if (id && canMoveUp) actions?.moveElementLayer?.(id, "up");
                }}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && id && canMoveUp) {
                    e.preventDefault();
                    actions?.moveElementLayer?.(id, "up");
                  }
                }}
                style={{
                  cursor: canMoveUp ? "pointer" : "not-allowed",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28,
                  height: 28,
                  borderRadius: 4,
                  background: "rgba(0,0,0,0.65)",
                  color: canMoveUp ? "#fff" : "rgba(255,255,255,0.4)",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                }}
                title="Bring layer up"
              >
                <ArrowUpwardIcon sx={{ fontSize: 18 }} />
              </div>
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  if (id && canMoveDown) actions?.moveElementLayer?.(id, "down");
                }}
                onKeyDown={(e) => {
                  if ((e.key === "Enter" || e.key === " ") && id && canMoveDown) {
                    e.preventDefault();
                    actions?.moveElementLayer?.(id, "down");
                  }
                }}
                style={{
                  cursor: canMoveDown ? "pointer" : "not-allowed",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28,
                  height: 28,
                  borderRadius: 4,
                  background: "rgba(0,0,0,0.65)",
                  color: canMoveDown ? "#fff" : "rgba(255,255,255,0.4)",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                }}
                title="Bring layer down"
              >
                <ArrowDownwardIcon sx={{ fontSize: 18 }} />
              </div>
              <div
                role="button"
                tabIndex={0}
                onClick={(e) => {
                  e.stopPropagation();
                  if (id) actions?.deleteElementById(id);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (id) actions?.deleteElementById(id);
                  }
                }}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 28,
                  height: 28,
                  borderRadius: 4,
                  background: "rgba(0,0,0,0.65)",
                  color: "#fff",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
                }}
                title="Delete"
              >
                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
              </div>
                </>
              )}
            </div>
          )}
          <div
            className="item-container"
            style={{
              position: "relative",
              width: "100%",
              height: "100%",
              zIndex: 0,
            }}
          >
            {isDraggingActive && (type === "SLIDE" || type === "3D_MODEL") && (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  zIndex: 2,
                  background: "transparent",
                  cursor: "move",
                }}
              />
            )}
            {getComponent()}
          </div>
        </div>
      </Rnd>
    </div>
  );
};

export default CanvasComponent;
